<template>
  <div class="dp-wrapper">
    <div class="dp-input-group">
      <input
        ref="input"
        type="text"
        class="form-control ln-border"
        :id="inputId"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :value="inputText"
        autocomplete="off"
        @change="onManualInput"
        @keyup.enter.stop="openCalendarOnEnter"
        @keydown.delete="onDeleteKey"
        @keydown.backspace="onDeleteKey"
      />
      <button
        type="button"
        class="dp-clear-btn"
        :aria-label="`Išvalyti ${ariaLabel}`"
        @click="clearValue"
        @keyup.enter.stop
        @keyup.space.stop
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <button
        type="button"
        class="dp-calendar-btn"
        :aria-label="`Atidaryti ${ariaLabel} kalendorių`"
        @click="toggle"
        @keyup.enter.stop
        @keyup.space.stop
      >
        <font-awesome-icon :icon="['fas', 'calendar']" aria-hidden="true" />
      </button>
    </div>
    <div v-if="isOpen" class="dp-popover" @keyup.enter.stop @keyup.space.stop>
      <div class="dp-nav-header">
        <select v-model.number="currentMonth" class="form-control form-control-sm" aria-label="Mėnuo">
          <option v-for="(name, idx) in monthNames" :key="idx" :value="idx + 1">{{ name }}</option>
        </select>
        <select v-model.number="currentYear" class="form-control form-control-sm" aria-label="Metai">
          <option v-for="y in yearRange" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
      <v-calendar
        :from-page="page"
        :attributes="selectedAttrs"
        locale="lt"
        is-expanded
        @update:from-page="onPageUpdate"
        @dayclick="onDayClick"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'DatePickerInput',
  model: { prop: 'value', event: 'input' },
  props: {
    value:       { type: String, default: '' },
    placeholder: { type: String, default: '' },
    ariaLabel:   { type: String, default: '' },
    inputId:     { type: String, default: '' },
    minYear:     { type: Number, default: 1990 },
  },
  data() {
    const now = new Date();
    return {
      isOpen: false,
      inputText: '',
      currentMonth: now.getMonth() + 1,
      currentYear: now.getFullYear(),
      monthNames: ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'],
    };
  },
  computed: {
    page() {
      return { month: this.currentMonth, year: this.currentYear };
    },
    yearRange() {
      const y = new Date().getFullYear();
      const range = [];
      for (let i = this.minYear; i <= y + 1; i++) range.push(i);
      return range;
    },
    selectedAttrs() {
      if (!this.value) return [{ key: 'today', highlight: true, dates: new Date() }];
      const d = new Date(this.value);
      return isNaN(d) ? [] : [{ key: 'selected', highlight: true, dates: d }];
    },
  },
  watch: {
    value(val) {
      this.inputText = val;
    },
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          const first = this.$el.querySelector('.dp-popover select');
          if (first) first.focus();
          document.addEventListener('keydown', this.trapFocus);
        });
      } else {
        document.removeEventListener('keydown', this.trapFocus);
        this.$refs.input.focus();
      }
    },
  },
  mounted() {
    this.inputText = this.value;
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('keydown', this.trapFocus);
  },
  methods: {
    openCalendarOnEnter() {
      if (this._suppressEnterOpen) { this._suppressEnterOpen = false; return; }
      if (!this.isOpen) this.toggle();
    },
    toggle() {
      if (!this.isOpen && this.value) {
        const d = new Date(this.value);
        if (!isNaN(d)) {
          this.currentMonth = d.getMonth() + 1;
          this.currentYear = d.getFullYear();
        }
      }
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
    },
    clearValue(evt) {
      this.$emit('input', '');
      this.inputText = '';
      if (evt && evt.detail === 0) {
        this._suppressEnterOpen = true;
      }
      this.$refs.input.focus();
    },
    onDeleteKey(e) {
      const el = e.target;
      if (el.selectionStart === 0 && el.selectionEnd === el.value.length) {
        this.$emit('input', '');
        this.inputText = '';
        e.preventDefault();
      }
    },
    onManualInput(e) {
      const raw = e.target.value.trim();
      if (!raw) {
        this.$emit('input', '');
        this.inputText = '';
        return;
      }
      const parsed = this.parseDate(raw);
      if (parsed) {
        this.$emit('input', parsed);
        this.inputText = parsed;
        e.target.value = parsed;
      } else {
        this.inputText = this.value;
        e.target.value = this.value;
      }
    },
    parseDate(str) {
      const fmtISO = /^(\d{4})[-.](\d{2})[-.](\d{2})$/;
      const fmtLT  = /^(\d{2})[-.](\d{2})[-.](\d{4})$/;
      let y, m, d;
      if (fmtISO.test(str)) {
        [, y, m, d] = str.match(fmtISO);
      } else if (fmtLT.test(str)) {
        [, d, m, y] = str.match(fmtLT);
      } else {
        return null;
      }
      const date = new Date(`${y}-${m}-${d}`);
      if (isNaN(date)) return null;
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    },
    onOutsideClick(e) {
      if (!this.$el.contains(e.target)) this.isOpen = false;
    },
    onPageUpdate({ month, year }) {
      this.currentMonth = month;
      this.currentYear = year;
    },
    onDayClick({ date }) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const val = `${y}-${m}-${d}`;
      this.$emit('input', val);
      this.inputText = val;
      this.isOpen = false;
    },
    trapFocus(e) {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.isOpen = false;
        return;
      }

      if (e.key === 'Enter') {
        const popover = this.$el.querySelector('.dp-popover');
        const active = document.activeElement;
        if (popover && popover.contains(active)) {
          e.preventDefault();
          if (active.tagName !== 'SELECT') {
            active.click();
            if (active.classList.contains('vc-day-content')) {
              this._suppressEnterOpen = true;
              this.$nextTick(() => { this.isOpen = false; });
            }
          }
        }
        return;
      }

      if (e.key !== 'Tab') return;

      const popover = this.$el.querySelector('.dp-popover');
      if (!popover) return;

      const focusable = Array.from(popover.querySelectorAll(
        'button:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
      )).filter(el => el.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
  },
};
</script>

<style scoped>
.dp-wrapper {
  position: relative;
}
.dp-input-group {
  display: flex;
  align-items: center;
}
.dp-input-group input {
  flex: 1;
  border-radius: 4px 0 0 4px;
}
.dp-clear-btn,
.dp-calendar-btn {
  background: #fff;
  border: 1px solid #6c757d;
  border-left: none;
  color: #6c757d;
  padding: 0 8px;
  height: calc(1.5em + .75rem + 2px);
  cursor: pointer;
  line-height: 1;
  font-size: 1rem;
  display: flex;
  align-items: center;
}
.dp-calendar-btn {
  border-radius: 0 4px 4px 0;
}
.dp-clear-btn:hover,
.dp-calendar-btn:hover {
  color: rgb(0, 92, 191);
}
.dp-clear-btn:focus,
.dp-calendar-btn:focus {
  color: rgb(0, 92, 191);
  border: 2px solid rgb(0, 92, 191);
  outline: none;
  box-shadow: none;
}
.dp-wrapper input::placeholder {
  color: #495057;
  opacity: 1;
}
.dp-popover {
  position: absolute;
  z-index: 1050;
  background: #fff;
  border: 1px solid #6c757d;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 8px;
  width: 276px;
  box-sizing: border-box;
}
@media (max-width: 767.98px) {
  .dp-popover {
    position: fixed;
    left: 8px;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: auto;
    max-height: 90vh;
    overflow-y: auto;
  }
}
.dp-nav-header {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}
.dp-nav-header select {
  flex: 1;
  color: #495057;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right .5rem center;
  background-size: 8px 10px;
  padding-right: 1.5rem;
}
.dp-nav-header select:focus {
  border: 2px solid rgb(0, 92, 191);
  box-shadow: none;
  outline: none;
}
::v-deep .vc-title {
  text-transform: capitalize;
  color: #333;
}
::v-deep .vc-arrow {
  color: rgb(0, 92, 191);
  font-size: 1rem;
}
::v-deep .vc-weekday {
  color: #333;
}
::v-deep .vc-day-content {
  color: #333;
}
::v-deep .vc-day:has(.vc-highlight) .vc-day-content {
  color: #fff !important;
}
::v-deep .vc-day-content:hover,
::v-deep .vc-day-content:focus,
::v-deep .vc-day-content.is-focused {
  background-color: rgb(0, 92, 191) !important;
  color: #fff !important;
  border-radius: 50%;
}
</style>
